import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaChevronDown, FaSearch, FaCheck } from "react-icons/fa";

/**
 * Drop-in replacement for a native <select> with an in-panel search box.
 * Accepts options as [{ value, label }] or plain strings/numbers.
 */
const SearchableSelect = ({
    value,
    onChange,
    options = [],
    placeholder = "Select…",
    searchPlaceholder = "Search…",
    className = "",
    panelClassName = "",
    disabled = false,
    error = false,
    id,
    name,
    allowClear = false,
}) => {
    const normalized = useMemo(
        () => options.map((o) => (typeof o === "object" && o !== null ? o : { value: o, label: String(o) })),
        [options]
    );

    const [open, setOpen]     = useState(false);
    const [search, setSearch] = useState("");
    const [highlight, setHighlight] = useState(0);

    const rootRef   = useRef(null);
    const inputRef  = useRef(null);
    const listRef   = useRef(null);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return normalized;
        return normalized.filter((o) => o.label?.toLowerCase().includes(term));
    }, [normalized, search]);

    const selected = normalized.find((o) => String(o.value) === String(value));

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    useEffect(() => {
        if (open) {
            setSearch("");
            setHighlight(0);
            const t = setTimeout(() => inputRef.current?.focus(), 0);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [open]);

    useEffect(() => {
        setHighlight(0);
    }, [search]);

    useEffect(() => {
        if (!open) return;
        const el = listRef.current?.children?.[highlight];
        el?.scrollIntoView({ block: "nearest" });
    }, [highlight, open]);

    const commit = (opt) => {
        if (opt.disabled) return;
        onChange?.(opt.value);
        setOpen(false);
    };

    const onKeyDown = (e) => {
        if (e.key === "Escape") { setOpen(false); return; }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, filtered.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (filtered[highlight]) commit(filtered[highlight]);
        }
    };

    return (
        <div ref={rootRef} className="relative">
            <button
                type="button"
                id={id}
                name={name}
                disabled={disabled}
                onClick={() => !disabled && setOpen((o) => !o)}
                className={`w-full flex items-center justify-between gap-2 text-left disabled:opacity-60 disabled:cursor-not-allowed ${
                    error ? "border-red-400" : "border-gray-200"
                } ${className}`}
            >
                <span className={`truncate ${selected ? "text-gray-800" : "text-gray-400"}`}>
                    {selected ? selected.label : placeholder}
                </span>
                <FaChevronDown className={`flex-shrink-0 text-gray-400 text-[10px] transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && !disabled && (
                <div className={`absolute z-50 mt-1.5 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden ${panelClassName}`}>
                    <div className="relative border-b border-gray-100">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-[11px]" />
                        <input
                            ref={inputRef}
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={onKeyDown}
                            placeholder={searchPlaceholder}
                            className="w-full pl-8 pr-3 py-2.5 text-sm focus:outline-none placeholder:text-gray-400"
                        />
                    </div>
                    <div ref={listRef} className="max-h-56 overflow-y-auto py-1">
                        {allowClear && (
                            <button
                                type="button"
                                onClick={() => commit({ value: "", label: placeholder })}
                                className="w-full flex items-center px-3 py-2 text-sm text-gray-400 hover:bg-gray-50 text-left"
                            >
                                {placeholder}
                            </button>
                        )}
                        {filtered.length === 0 ? (
                            <p className="px-3 py-3 text-xs text-gray-400 text-center">No matches</p>
                        ) : (
                            filtered.map((opt, i) => {
                                const isSelected = String(opt.value) === String(value);
                                return (
                                    <button
                                        type="button"
                                        key={`${opt.value}-${i}`}
                                        disabled={opt.disabled}
                                        onMouseEnter={() => !opt.disabled && setHighlight(i)}
                                        onClick={() => !opt.disabled && commit(opt)}
                                        className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left transition-colors ${
                                            opt.disabled
                                                ? "text-gray-300 cursor-not-allowed"
                                                : i === highlight ? "bg-primary/10" : "hover:bg-gray-50"
                                        } ${isSelected ? "font-semibold text-primary" : !opt.disabled ? "text-gray-700" : ""}`}
                                    >
                                        <span className="truncate">{opt.label}</span>
                                        {isSelected && <FaCheck className="flex-shrink-0 text-[10px]" />}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;
